import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Sidebar from '../Sidebar';
import Header from '../Header';
import PageHeader from '../ui/PageHeader';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  ShieldCheck, Smartphone, MonitorSmartphone, Settings2, CalendarDays,
  Youtube, ListChecks, TrendingDown, Clock, Check, QrCode,
  Rocket, RotateCcw, Sparkles, AlertTriangle, BookOpen, Globe,
} from 'lucide-react';

const CHECKLIST_KEY = 'dp_trust_score_checklist';

// Cor de destaque por etapa — toda dentro da paleta do app (mesma familia dos
// tiers do Niche Finder). Icone lucide por etapa, no padrao azul das secoes.
const STEP_META = [
  { color: '#0DAEFF', Icon: Smartphone },        // 01 Gmail
  { color: '#06B6D4', Icon: Settings2 },         // 02 Personalizacao
  { color: '#8B5CF6', Icon: CalendarDays },      // 03 Aquecimento Google
  { color: '#34D399', Icon: Youtube },           // 04 Canal + upload
  { color: '#F59E0B', Icon: ListChecks },        // 05 Checklist
  { color: '#EF4444', Icon: TrendingDown },      // 06 Redutores de score
  { color: '#3B82F6', Icon: Clock },             // 07 Linha do tempo
];

// ── Motion: reveal padrao (sobe + fade) ao entrar na viewport ──
const reveal = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: i * 0.05 },
  }),
};

const MotionSection = ({ children, refEl, ...rest }) => (
  <motion.section
    ref={refEl}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: '-70px' }}
    variants={reveal}
    style={{ scrollMarginTop: '84px' }}
    {...rest}
  >
    {children}
  </motion.section>
);

// ── Cartao base (mesmo vocabulario visual dos cards do app) ──
const cardBase = {
  background: 'var(--dp-bg-card)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: '16px',
  fontFamily: 'var(--dp-font)',
};

// Cabecalho numerado de cada etapa
const StepHead = ({ n, tag, title, color, Icon }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
    <div style={{
      position: 'relative', width: '52px', height: '52px', borderRadius: '14px', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}14`, border: `1px solid ${color}33`,
    }}>
      <Icon size={22} style={{ color }} />
      <span style={{
        position: 'absolute', top: '-8px', right: '-8px',
        fontSize: '10px', fontWeight: 800, letterSpacing: '0.4px',
        color: '#fff', background: color, borderRadius: '7px', padding: '2px 6px',
        boxShadow: `0 3px 10px ${color}55`,
      }}>{n}</span>
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{
        fontSize: '11px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase',
        color, marginBottom: '3px',
      }}>{tag}</div>
      <h2 style={{
        margin: 0, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.4px',
        color: 'var(--dp-text-primary)', lineHeight: 1.2,
      }}>{title}</h2>
    </div>
  </div>
);

// Bloco interno com titulo pequeno (subsecao dentro do card da etapa)
const SubHead = ({ children, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px',
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
    color: 'var(--dp-text-tertiary)',
  }}>
    <span style={{ width: '5px', height: '5px', borderRadius: '999px', background: color }} />
    {children}
  </div>
);

// Item de lista com marcador check
const Bullet = ({ children, color }) => (
  <li style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '9px' }}>
    <span style={{
      flexShrink: 0, marginTop: '2px', width: '18px', height: '18px', borderRadius: '6px',
      background: `${color}18`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Check size={12} style={{ color }} />
    </span>
    <span style={{ fontSize: '14px', lineHeight: 1.55, color: 'var(--dp-text-secondary)' }}>{children}</span>
  </li>
);

const arr = (t, key) => {
  const v = t(key, { returnObjects: true });
  return Array.isArray(v) ? v : [];
};

const TrustScorePage = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('trustScore.title'));
  const { sidebarWidth, isMobile } = useSidebar();

  const pillars = arr(t, 'trustScore.pillars');
  const stepper = arr(t, 'trustScore.stepper');
  const phoneLadder = arr(t, 'trustScore.s1_phone_ladder');
  const personalization = arr(t, 'trustScore.s2_settings');
  const googleDays = arr(t, 'trustScore.s3_days');
  const killers = arr(t, 'trustScore.s6_killers');
  const timeline = arr(t, 'trustScore.s7_timeline');
  const checklistItems = arr(t, 'trustScore.s5_checklist');

  // Refs para o stepper rolar ate cada etapa
  const sectionRefs = useRef([]);
  const [activeStep, setActiveStep] = useState(0);
  const scrollTo = (i) => {
    sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Observa qual etapa esta visivel para destacar o pill correspondente
  useEffect(() => {
    const els = sectionRefs.current.filter(Boolean);
    if (els.length === 0) return undefined;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const idx = Number(e.target.getAttribute('data-step'));
          if (!Number.isNaN(idx)) setActiveStep(idx);
        }
      });
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [stepper.length]);

  // Checklist interativo persistido no navegador (mesmo padrao do "visited"
  // do Niche Finder). Guarda um array de booleans do tamanho do checklist.
  const [checks, setChecks] = useState(() => {
    try {
      const raw = localStorage.getItem(CHECKLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const toggleCheck = useCallback((i) => {
    setChecks((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      try { localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const resetChecks = useCallback(() => {
    setChecks([]);
    try { localStorage.removeItem(CHECKLIST_KEY); } catch { /* ignore */ }
  }, []);
  const doneCount = useMemo(
    () => checklistItems.reduce((acc, _, i) => acc + (checks[i] ? 1 : 0), 0),
    [checks, checklistItems]
  );
  const donePct = checklistItems.length > 0 ? Math.round((doneCount / checklistItems.length) * 100) : 0;

  const setRef = (i) => (el) => { sectionRefs.current[i] = el; };

  // Cor por "tier" da escala de telefone
  const ladderColor = (tier) => ({
    best: '#34D399', good: '#0DAEFF', ok: '#F59E0B', avoid: '#EF4444',
  }[tier] || 'var(--dp-text-tertiary)');
  const ladderLabel = (tier) => ({
    best: t('trustScore.tier_best'), good: t('trustScore.tier_good'),
    ok: t('trustScore.tier_ok'), avoid: t('trustScore.tier_avoid'),
  }[tier] || '');

  const gridCols = (min) => ({
    display: 'grid', gap: '14px',
    gridTemplateColumns: isMobile ? '1fr' : `repeat(auto-fit, minmax(${min}, 1fr))`,
  });

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px', flex: 1, minWidth: 0,
        transition: 'margin-left 0.3s ease', animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        <PageHeader
          icon={<ShieldCheck />}
          title={t('trustScore.title')}
          description={t('trustScore.subtitle')}
          badge={t('trustScore.badge')}
        />

        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

          {/* ── HERO / INTRO ── */}
          <motion.div
            initial="hidden" animate="show" variants={reveal}
            style={{
              ...cardBase, position: 'relative', overflow: 'hidden',
              padding: isMobile ? '22px' : '30px 32px', marginBottom: '16px',
            }}
          >
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'radial-gradient(120% 90% at 100% 0%, rgba(13,174,255,0.10), transparent 60%)',
            }} />
            <div style={{ position: 'relative' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px', marginBottom: '14px',
                fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                color: 'var(--dp-blue-500)', background: 'rgba(13,174,255,0.08)',
                border: '1px solid rgba(13,174,255,0.2)', padding: '5px 12px', borderRadius: '999px',
              }}>
                <Sparkles size={13} /> {t('trustScore.hero_kicker')}
              </div>
              <h2 style={{
                margin: '0 0 14px', fontSize: isMobile ? '24px' : '30px', fontWeight: 800,
                letterSpacing: '-0.6px', color: 'var(--dp-text-primary)', lineHeight: 1.15,
              }}>
                {t('trustScore.hero_title')}
              </h2>
              {['hero_p1', 'hero_p2', 'hero_p3'].map((k, i) => (
                <p key={k} style={{
                  margin: '0 0 12px', fontSize: '15px', lineHeight: 1.65,
                  color: i === 0 ? 'var(--dp-text-primary)' : 'var(--dp-text-secondary)',
                  fontWeight: i === 0 ? 600 : 400,
                  maxWidth: '760px',
                }}>
                  {t(`trustScore.${k}`)}
                </p>
              ))}

              {/* Enquadramento: MANUAL da comunidade, nao regra oficial */}
              <div style={{
                display: 'flex', gap: '12px', alignItems: 'flex-start', marginTop: '18px',
                padding: '14px 16px', borderRadius: '12px', maxWidth: '760px',
                background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)',
              }}>
                <BookOpen size={18} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#F59E0B', marginBottom: '4px' }}>
                    {t('trustScore.manual_note_title')}
                  </div>
                  <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--dp-text-secondary)' }}>
                    {t('trustScore.manual_note_text')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── PILARES (4 cards) ── */}
          <div style={{ ...gridCols('220px'), marginBottom: '22px' }}>
            {pillars.map((p, i) => (
              <motion.div
                key={p.label}
                custom={i}
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={reveal}
                style={{ ...cardBase, padding: '16px 18px' }}
              >
                <div style={{
                  fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase',
                  color: 'var(--dp-blue-500)', marginBottom: '8px',
                }}>{p.label}</div>
                <div style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'var(--dp-text-secondary)' }}>
                  {p.value}
                </div>
              </motion.div>
            ))}
          </div>

          {/* ── STEPPER NAV (pills que rolam ate a etapa) ── */}
          <div style={{
            position: 'sticky', top: '68px', zIndex: 20,
            background: 'var(--dp-bg-primary)', paddingBottom: '10px', marginBottom: '10px',
          }}>
            <div style={{
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
              color: 'var(--dp-text-tertiary)', margin: '0 2px 8px',
            }}>
              {t('trustScore.stepper_title')}
            </div>
            <div className="dp-thin-scrollbar" style={{
              display: 'flex', gap: '7px', overflowX: 'auto', paddingBottom: '4px',
            }}>
              {stepper.map((s, i) => {
                const active = activeStep === i;
                const c = STEP_META[i]?.color || 'var(--dp-blue-500)';
                return (
                  <button
                    key={s.n}
                    onClick={() => scrollTo(i)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '7px', flexShrink: 0,
                      padding: '7px 13px', borderRadius: '999px', cursor: 'pointer',
                      fontFamily: 'var(--dp-font)', fontSize: '12.5px', fontWeight: active ? 700 : 500,
                      whiteSpace: 'nowrap', transition: 'all 0.2s',
                      background: active ? `${c}1A` : 'transparent',
                      color: active ? c : 'var(--dp-text-secondary)',
                      border: `1px solid ${active ? `${c}59` : 'var(--dp-border-subtle)'}`,
                    }}
                  >
                    <span style={{
                      fontSize: '10px', fontWeight: 800,
                      color: active ? c : 'var(--dp-text-tertiary)',
                    }}>{s.n}</span>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ══════════ ETAPA 01 — GMAIL ══════════ */}
          <MotionSection refEl={setRef(0)} data-step={0} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="01" tag={t('trustScore.s1_tag')} title={t('trustScore.s1_title')} color={STEP_META[0].color} Icon={MonitorSmartphone} />
            <p style={introStyle}>{t('trustScore.s1_intro')}</p>

            <div style={{ ...gridCols('300px'), marginTop: '18px' }}>
              <div style={innerCard}>
                <SubHead color={STEP_META[0].color}>{t('trustScore.s1_device_head')}</SubHead>
                <ul style={ulReset}>
                  {arr(t, 'trustScore.s1_device_items').map((it, i) => (
                    <Bullet key={i} color={STEP_META[0].color}>{it}</Bullet>
                  ))}
                </ul>
              </div>
              <div style={innerCard}>
                <SubHead color={STEP_META[0].color}>{t('trustScore.s1_create_head')}</SubHead>
                <ul style={ulReset}>
                  {arr(t, 'trustScore.s1_create_items').map((it, i) => (
                    <Bullet key={i} color={STEP_META[0].color}>{it}</Bullet>
                  ))}
                </ul>
              </div>
            </div>

            {/* Escala de qualidade do telefone (com chip BR no topo) */}
            <div style={{ ...innerCard, marginTop: '14px' }}>
              <SubHead color={STEP_META[0].color}>
                <Smartphone size={13} style={{ marginRight: '2px' }} /> {t('trustScore.s1_phone_head')}
              </SubHead>
              <p style={{ ...introStyle, fontSize: '13.5px', margin: '0 0 14px' }}>{t('trustScore.s1_phone_intro')}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {phoneLadder.map((row, i) => {
                  const c = ladderColor(row.tier);
                  return (
                    <div key={i} style={{
                      display: 'flex', gap: '12px', alignItems: 'flex-start',
                      padding: '11px 13px', borderRadius: '11px',
                      background: 'var(--dp-bg-secondary)', border: `1px solid ${c}2E`,
                      borderLeft: `3px solid ${c}`,
                    }}>
                      <span style={{
                        flexShrink: 0, fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.5px',
                        textTransform: 'uppercase', color: c, background: `${c}1A`,
                        padding: '3px 8px', borderRadius: '6px', marginTop: '1px', minWidth: '58px', textAlign: 'center',
                      }}>{ladderLabel(row.tier)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--dp-text-primary)', lineHeight: 1.4 }}>{row.label}</div>
                        <div style={{ fontSize: '12.5px', color: 'var(--dp-text-tertiary)', lineHeight: 1.45, marginTop: '2px' }}>{row.note}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{
                display: 'flex', gap: '9px', alignItems: 'flex-start', marginTop: '13px',
                padding: '11px 13px', borderRadius: '11px',
                background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.2)',
              }}>
                <Globe size={16} style={{ color: '#34D399', flexShrink: 0, marginTop: '1px' }} />
                <span style={{ fontSize: '13px', lineHeight: 1.5, color: 'var(--dp-text-secondary)' }}>{t('trustScore.s1_phone_tip')}</span>
              </div>
            </div>

            {/* QR + Evite */}
            <div style={{ ...gridCols('300px'), marginTop: '14px' }}>
              <div style={{ ...innerCard, background: 'rgba(13,174,255,0.05)', borderColor: 'rgba(13,174,255,0.2)' }}>
                <SubHead color={STEP_META[0].color}><QrCode size={13} style={{ marginRight: '2px' }} /> {t('trustScore.s1_qr_head')}</SubHead>
                <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--dp-text-secondary)' }}>{t('trustScore.s1_qr_text')}</p>
              </div>
              <div style={{ ...innerCard, background: 'rgba(239,68,68,0.05)', borderColor: 'rgba(239,68,68,0.22)' }}>
                <SubHead color="#EF4444"><AlertTriangle size={13} style={{ marginRight: '2px' }} /> {t('trustScore.s1_avoid_head')}</SubHead>
                <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--dp-text-secondary)' }}>{t('trustScore.s1_avoid_text')}</p>
              </div>
            </div>
          </MotionSection>

          {/* ══════════ ETAPA 02 — PERSONALIZACAO ══════════ */}
          <MotionSection refEl={setRef(1)} data-step={1} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="02" tag={t('trustScore.s2_tag')} title={t('trustScore.s2_title')} color={STEP_META[1].color} Icon={STEP_META[1].Icon} />
            <p style={introStyle}>{t('trustScore.s2_intro')}</p>

            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
              margin: '16px 0', padding: '9px 14px', borderRadius: '10px',
              background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
            }}>
              <span style={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--dp-text-tertiary)' }}>
                {t('trustScore.s2_path_label')}
              </span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>{t('trustScore.s2_path_value')}</span>
            </div>

            <div style={innerCard}>
              <SubHead color={STEP_META[1].color}>{t('trustScore.s2_settings_head')}</SubHead>
              <div style={{ display: 'grid', gap: '8px', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
                {personalization.map((row, i) => {
                  const on = /^on$/i.test(String(row.status).trim());
                  const c = on ? '#34D399' : '#8B5CF6';
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                      padding: '10px 13px', borderRadius: '10px',
                      background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
                    }}>
                      <span style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--dp-text-primary)' }}>{row.name}</span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px', flexShrink: 0,
                        fontSize: '11px', fontWeight: 700, color: c,
                        background: `${c}18`, border: `1px solid ${c}3A`, padding: '3px 9px', borderRadius: '999px',
                      }}>
                        {on && <Check size={11} />}{row.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ ...innerCard, marginTop: '14px', background: 'rgba(6,182,212,0.05)', borderColor: 'rgba(6,182,212,0.2)' }}>
              <SubHead color={STEP_META[1].color}>{t('trustScore.s2_why_head')}</SubHead>
              <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--dp-text-secondary)' }}>{t('trustScore.s2_why_text')}</p>
            </div>
          </MotionSection>

          {/* ══════════ ETAPA 03 — AQUECIMENTO GOOGLE ══════════ */}
          <MotionSection refEl={setRef(2)} data-step={2} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="03" tag={t('trustScore.s3_tag')} title={t('trustScore.s3_title')} color={STEP_META[2].color} Icon={STEP_META[2].Icon} />
            <p style={introStyle}>{t('trustScore.s3_intro')}</p>

            <div style={{ ...innerCard, marginTop: '16px', background: 'rgba(139,92,246,0.06)', borderColor: 'rgba(139,92,246,0.22)' }}>
              <SubHead color={STEP_META[2].color}>{t('trustScore.s3_rule_head')}</SubHead>
              <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--dp-text-secondary)' }}>{t('trustScore.s3_rule_text')}</p>
            </div>

            {/* Trilha de dias — mini timeline vertical */}
            <div style={{ marginTop: '18px', position: 'relative' }}>
              <div style={{
                position: 'absolute', left: '13px', top: '10px', bottom: '10px', width: '2px',
                background: 'linear-gradient(180deg, #8B5CF6, rgba(139,92,246,0.15))',
              }} />
              {googleDays.map((d, i) => (
                <motion.div
                  key={i} custom={i}
                  initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
                  style={{ display: 'flex', gap: '14px', marginBottom: '12px', position: 'relative' }}
                >
                  <div style={{
                    flexShrink: 0, width: '28px', height: '28px', borderRadius: '999px', zIndex: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--dp-bg-card)', border: '2px solid #8B5CF6',
                    fontSize: '11px', fontWeight: 800, color: '#8B5CF6',
                  }}>{i + 1}</div>
                  <div style={{ ...innerCard, flex: 1, padding: '12px 15px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#8B5CF6', marginBottom: '3px' }}>{d.days}</div>
                    <div style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'var(--dp-text-secondary)' }}>{d.activity}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div style={{ ...innerCard, marginTop: '8px' }}>
              <SubHead color={STEP_META[2].color}>{t('trustScore.s3_guidelines_head')}</SubHead>
              <ul style={ulReset}>
                {arr(t, 'trustScore.s3_guidelines').map((g, i) => (
                  <Bullet key={i} color={STEP_META[2].color}>{g}</Bullet>
                ))}
              </ul>
            </div>
          </MotionSection>

          {/* ══════════ ETAPA 04 — CANAL + UPLOAD ══════════ */}
          <MotionSection refEl={setRef(3)} data-step={3} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="04" tag={t('trustScore.s4_tag')} title={t('trustScore.s4_title')} color={STEP_META[3].color} Icon={STEP_META[3].Icon} />
            <p style={introStyle}>{t('trustScore.s4_intro')}</p>

            <div style={{ ...gridCols('300px'), marginTop: '18px' }}>
              <div style={innerCard}>
                <SubHead color={STEP_META[3].color}>{t('trustScore.s4_create_head')}</SubHead>
                <ul style={ulReset}>
                  {arr(t, 'trustScore.s4_create_items').map((it, i) => (
                    <Bullet key={i} color={STEP_META[3].color}>{it}</Bullet>
                  ))}
                </ul>
              </div>
              <div style={innerCard}>
                <SubHead color={STEP_META[3].color}>{t('trustScore.s4_warmup_head')}</SubHead>
                <p style={{ margin: '0 0 12px', fontSize: '12.5px', lineHeight: 1.5, color: 'var(--dp-text-tertiary)' }}>{t('trustScore.s4_warmup_sub')}</p>
                <ul style={ulReset}>
                  {arr(t, 'trustScore.s4_warmup_items').map((it, i) => (
                    <Bullet key={i} color={STEP_META[3].color}>{it}</Bullet>
                  ))}
                </ul>
              </div>
            </div>

            <div style={{
              ...innerCard, marginTop: '14px', display: 'flex', gap: '14px', alignItems: 'flex-start',
              background: 'rgba(52,211,153,0.06)', borderColor: 'rgba(52,211,153,0.22)',
            }}>
              <div style={{
                flexShrink: 0, width: '42px', height: '42px', borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)',
              }}>
                <Rocket size={20} style={{ color: '#34D399' }} />
              </div>
              <div>
                <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--dp-text-primary)', marginBottom: '5px' }}>{t('trustScore.s4_first_head')}</div>
                <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--dp-text-secondary)' }}>{t('trustScore.s4_first_text')}</p>
              </div>
            </div>
          </MotionSection>

          {/* ══════════ ETAPA 05 — CHECKLIST INTERATIVO ══════════ */}
          <MotionSection refEl={setRef(4)} data-step={4} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="05" tag={t('trustScore.s5_tag')} title={t('trustScore.s5_title')} color={STEP_META[4].color} Icon={STEP_META[4].Icon} />
            <p style={introStyle}>{t('trustScore.s5_intro')}</p>

            {/* Barra de progresso do checklist */}
            <div style={{ margin: '18px 0 14px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '180px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--dp-text-tertiary)', marginBottom: '6px' }}>
                  <span>{t('trustScore.s5_progress_label')}</span>
                  <span style={{ fontWeight: 700, color: donePct === 100 ? '#34D399' : '#F59E0B' }}>{doneCount}/{checklistItems.length}</span>
                </div>
                <div style={{ height: '8px', borderRadius: '999px', background: 'var(--dp-bg-input)', overflow: 'hidden' }}>
                  <motion.div
                    animate={{ width: `${donePct}%` }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    style={{ height: '100%', borderRadius: '999px', background: donePct === 100 ? 'linear-gradient(90deg,#34D399,#10B981)' : 'linear-gradient(90deg,#F59E0B,#FB923C)' }}
                  />
                </div>
              </div>
              <button
                onClick={resetChecks}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0,
                  padding: '7px 12px', borderRadius: '9px', cursor: 'pointer',
                  background: 'transparent', border: '1px solid var(--dp-border-medium)',
                  color: 'var(--dp-text-secondary)', fontSize: '12px', fontWeight: 500, fontFamily: 'var(--dp-font)',
                }}
              >
                <RotateCcw size={13} /> {t('trustScore.s5_reset')}
              </button>
            </div>

            {donePct === 100 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px',
                  padding: '11px 15px', borderRadius: '11px',
                  background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)',
                }}
              >
                <ShieldCheck size={18} style={{ color: '#34D399' }} />
                <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#34D399' }}>{t('trustScore.s5_done_label')}</span>
              </motion.div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {checklistItems.map((item, i) => {
                const on = !!checks[i];
                return (
                  <button
                    key={i}
                    onClick={() => toggleCheck(i)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left',
                      padding: '12px 14px', borderRadius: '11px', cursor: 'pointer',
                      background: on ? 'rgba(52,211,153,0.07)' : 'var(--dp-bg-secondary)',
                      border: `1px solid ${on ? 'rgba(52,211,153,0.28)' : 'var(--dp-border-subtle)'}`,
                      transition: 'all 0.18s', fontFamily: 'var(--dp-font)',
                    }}
                  >
                    <span style={{
                      flexShrink: 0, width: '22px', height: '22px', borderRadius: '7px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: on ? '#34D399' : 'transparent',
                      border: on ? '1px solid #34D399' : '1.5px solid var(--dp-border-medium)',
                      transition: 'all 0.18s',
                    }}>
                      {on && <Check size={14} style={{ color: '#fff' }} />}
                    </span>
                    <span style={{
                      fontSize: '13.5px', fontWeight: 500, lineHeight: 1.45,
                      color: on ? 'var(--dp-text-tertiary)' : 'var(--dp-text-primary)',
                      textDecoration: on ? 'line-through' : 'none',
                    }}>
                      <span style={{ fontWeight: 800, color: on ? 'var(--dp-text-tertiary)' : '#F59E0B', marginRight: '8px' }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {item}
                    </span>
                  </button>
                );
              })}
            </div>
          </MotionSection>

          {/* ══════════ ETAPA 06 — ERROS FATAIS ══════════ */}
          <MotionSection refEl={setRef(5)} data-step={5} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="06" tag={t('trustScore.s6_tag')} title={t('trustScore.s6_title')} color={STEP_META[5].color} Icon={STEP_META[5].Icon} />
            <p style={introStyle}>{t('trustScore.s6_intro')}</p>

            <div style={{ ...gridCols('280px'), marginTop: '18px' }}>
              {killers.map((k, i) => (
                <motion.div
                  key={k.n} custom={i % 3}
                  initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
                  whileHover={{ y: -3 }}
                  style={{
                    ...innerCard, position: 'relative', overflow: 'hidden',
                    paddingTop: '16px', borderColor: 'var(--dp-border-subtle)',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: '-6px', right: '10px', fontSize: '44px', fontWeight: 900,
                    color: 'rgba(239,68,68,0.09)', lineHeight: 1, userSelect: 'none',
                  }}>{k.n}</div>
                  <div style={{ position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                      <span style={{
                        width: '24px', height: '24px', borderRadius: '7px', flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(239,68,68,0.12)',
                      }}>
                        <TrendingDown size={13} style={{ color: '#EF4444' }} />
                      </span>
                      <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)', lineHeight: 1.3 }}>{k.title}</h3>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.55, color: 'var(--dp-text-secondary)' }}>{k.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </MotionSection>

          {/* ══════════ ETAPA 07 — LINHA DO TEMPO ══════════ */}
          <MotionSection refEl={setRef(6)} data-step={6} style={{ ...cardBase, padding: isMobile ? '22px' : '28px', marginBottom: '16px', scrollMarginTop: '150px' }}>
            <StepHead n="07" tag={t('trustScore.s7_tag')} title={t('trustScore.s7_title')} color={STEP_META[6].color} Icon={STEP_META[6].Icon} />
            <p style={introStyle}>{t('trustScore.s7_intro')}</p>

            <div style={{ marginTop: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {timeline.map((row, i) => (
                <motion.div
                  key={i} custom={i}
                  initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
                  style={{
                    display: 'flex', gap: isMobile ? '12px' : '18px', alignItems: 'stretch',
                    ...innerCard, padding: '0', overflow: 'hidden',
                  }}
                >
                  <div style={{
                    flexShrink: 0, width: isMobile ? '84px' : '112px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.14), rgba(13,174,255,0.06))',
                    borderRight: '1px solid var(--dp-border-subtle)',
                    fontSize: '13px', fontWeight: 800, color: '#3B82F6', letterSpacing: '-0.2px',
                  }}>{row.day}</div>
                  <div style={{ flex: 1, padding: '13px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--dp-text-primary)', marginBottom: '3px' }}>{row.action}</div>
                    <div style={{ fontSize: '13px', lineHeight: 1.5, color: 'var(--dp-text-tertiary)' }}>{row.detail}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Assinatura DarkPlanner (sem atribuicao externa) */}
            <div style={{
              marginTop: '20px', padding: '18px 20px', borderRadius: '14px', textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(13,174,255,0.08), rgba(59,130,246,0.05))',
              border: '1px solid rgba(13,174,255,0.2)',
            }}>
              <ShieldCheck size={22} style={{ color: 'var(--dp-blue-500)', marginBottom: '8px' }} />
              <p style={{ margin: 0, fontSize: '14.5px', fontWeight: 600, lineHeight: 1.55, color: 'var(--dp-text-primary)', maxWidth: '620px', marginInline: 'auto' }}>
                {t('trustScore.s7_footer')}
              </p>
            </div>
          </MotionSection>

          <div style={{ height: '24px' }} />
        </div>
      </main>
    </div>
  );
};

// ── estilos reaproveitados ──
const introStyle = {
  margin: 0, fontSize: '14.5px', lineHeight: 1.65, color: 'var(--dp-text-secondary)', maxWidth: '780px',
};
const innerCard = {
  background: 'var(--dp-bg-card-hover)', border: '1px solid var(--dp-border-subtle)',
  borderRadius: '14px', padding: '16px 18px',
};
const ulReset = { listStyle: 'none', margin: 0, padding: 0 };

export default TrustScorePage;
